import { SpaceBetween, BreadcrumbGroup, Flashbar, Header, Box, Table, Button } from '@amzn/awsui-components-console';
import React, { useEffect, useState } from 'react';
import { getHiddenToken, getTTLInDate } from './utils';
import { formatApiTokens, deleteApiToken } from './apis';
import * as LocalConstants from './constants';
import * as GlobalConstants from '../../lib/constants';

export default function ApiTokens() {
    const [securityNote, setSecurityNote] = React.useState([
        {
            type: 'info',
            dismissible: true,
            dismissLabel: 'Dismiss message',
            onDismiss: () => setSecurityNote([]),
            content: <>Do not share or distribute any tokens within or outside Amazon.</>,
        },
    ]);
    const [successMessage, setSuccessMessage] = React.useState([]);
    const [errorMessage, setErrorMessage] = React.useState([]);
    const [tokens, setTokens] = React.useState([]);
    const [selectedTokens, setSelectedTokens] = React.useState([]);
    const [hiddenTokens, setHiddenTokens] = React.useState([]);
    const [deleteDisabled, setDeleteDisabled] = React.useState(true);
    const [createDisabled, setCreateDisabled] = React.useState(true);
    const [loading, setLoading] = React.useState(false);
    const displayErrorContent = (content) => {
        // display error message
        setErrorMessage([
            {
                type: 'error',
                dismissible: true,
                dismissLabel: 'Dismiss message',
                onDismiss: () => setErrorMessage([]),
                content: <>{content}</>,
                action: (
                    <Button href={GlobalConstants.CONTACT_US} iconAlign="right" target="_blank">
                        Contact us
                    </Button>
                ),
            },
        ]);
    };
    const displaySuccessContent = (content) => {
        // display success message
        setSuccessMessage([
            {
                type: 'success',
                dismissible: true,
                dismissLabel: 'Dismiss message',
                onDismiss: () => setSuccessMessage([]),
                content: <>{content}</>,
            },
        ]);
    };
    const startLoading = () => {
        // initialize loading status
        setLoading(true);
        setErrorMessage([]);
        setSuccessMessage([]);
        setDeleteDisabled(true);
        setCreateDisabled(true);
    };
    const endLoading = (disableCreate) => {
        // end loading status
        setLoading(false);
        setCreateDisabled(disableCreate);
    };
    const loadTokens = () => {
        // load tokens in table
        (async function () {
            startLoading();
            let disableCreate = true;
            try {
                const apiTokens = await formatApiTokens();
                disableCreate = apiTokens.length < 2 ? false : true;
                setTokens(apiTokens);
                let hiddenApiTokens = JSON.parse(JSON.stringify(apiTokens));
                for (let index = 0; index < hiddenApiTokens.length; index++) {
                    let token = hiddenApiTokens[index];
                    token[LocalConstants.index] = index;
                    token[LocalConstants.token] = getHiddenToken(token[LocalConstants.token]);
                    token[LocalConstants.ttl] = getTTLInDate(token[LocalConstants.ttl]);
                }
                setHiddenTokens(hiddenApiTokens);
            } catch (e) {
                console.error('Fail to load user token(s) with error', e);
                displayErrorContent(
                    'Cannot load user token(s). Contact our dev team @ds2-device-farm-dev for more details.'
                );
            }
            endLoading(disableCreate);
        })();
    };
    const onSubmitDelete = () => {
        // when delete button is triggered
        (async function () {
            try {
                const selectedTokenIndex = selectedTokens[0][LocalConstants.index]; //only single selection is supported
                const selectedToken = tokens[selectedTokenIndex][LocalConstants.token];
                await deleteApiToken(selectedToken);
                loadTokens();
                displaySuccessContent(
                    `Successfully delete the selected token that ends with ${selectedToken.substring(
                        selectedToken.length - 5
                    )}`
                );
            } catch (e) {
                console.error('Fail to delete user token with error', e);
                displayErrorContent(
                    'Cannot delete user token. Contact our dev team @ds2-device-farm-dev for more details.'
                );
            }
        })();
    };
    const onSelectionChange = (selectedItems) => {
        // when token is selected
        setSelectedTokens(selectedItems);
        setDeleteDisabled(false);
    };
    useEffect(() => {
        // initialize the component
        loadTokens();
    }, []);
    return (
        <SpaceBetween size="s">
            <Flashbar items={securityNote} />
            <BreadcrumbGroup
                items={[
                    { text: 'Amazon Device Farm', href: '#' },
                    { text: 'Personal tokens', href: '#/api-auth/personal-tokens' },
                ]}
                ariaLabel="Breadcrumbs"
            />
            <Header
                variant="h1"
                description="Use personal API tokens that enables programmatic access to Amazon Device Farm for automated tasks and integrations. To protect your tokens, only the last 5 characters of them will be shown, copy to get the full tokens."
            >
                Personal tokens
            </Header>
            <Table
                onSelectionChange={({ detail }) => onSelectionChange(detail.selectedItems)}
                selectedItems={selectedTokens}
                selectionType="single"
                header={
                    <Header
                        counter={`(${hiddenTokens.length})`}
                        actions={
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button disabled={deleteDisabled} onClick={onSubmitDelete}>
                                    Delete token
                                </Button>
                                <Button
                                    disabled={createDisabled}
                                    href="#/api-auth/personal-tokens/create"
                                    variant="primary"
                                >
                                    Create token
                                </Button>
                            </SpaceBetween>
                        }
                    >
                        ADF API tokens
                    </Header>
                }
                resizableColumns
                columnDefinitions={[
                    {
                        id: LocalConstants.token,
                        header: 'Token',
                        cell: (e) => e.token,
                    },
                    {
                        id: LocalConstants.description,
                        header: 'Description',
                        cell: (e) => e.description,
                    },
                    {
                        id: LocalConstants.ttl,
                        header: 'When to expire',
                        cell: (e) => e.ttl,
                    },
                ]}
                items={hiddenTokens}
                empty={
                    <Box textAlign="center" color="inherit">
                        <b>No tokens to display</b>
                    </Box>
                }
                visibleColumns={[
                    LocalConstants.token,
                    LocalConstants.description,
                    LocalConstants.user,
                    LocalConstants.email,
                    LocalConstants.ttl,
                ]}
                loading={loading}
                loadingText="Loading tokens"
            />
            <Flashbar items={errorMessage} />
            <Flashbar items={successMessage} />
        </SpaceBetween>
    );
}
